<template>
  <Container hideFooter title="Admin dashboard">
    <template v-slot:body>
      <div class="admin-page">
        <div class="admin-heading">
          <div>
            <span class="section-overline">Operations</span>
            <h1>Admin dashboard</h1>
            <p>Review visitor activity and manage the front desk printer.</p>
          </div>
          <button type="button" class="refresh-button" @click="refreshData">
            <IconGlyph name="clock" :size="16" />
            <span>Refresh data</span>
          </button>
        </div>

        <div class="admin-layout">
          <aside class="admin-nav" aria-label="Admin sections">
            <span class="admin-nav-label">View</span>
            <button
              v-for="item in filters"
              :key="item.value"
              type="button"
              class="admin-nav-item"
              :class="{ 'admin-nav-item--active': filter === item.value }"
              @click="filter = item.value"
            >
              <IconGlyph :name="item.icon" :size="18" />
              <span>{{ item.text }}</span>
              <span v-if="filter === item.value" class="nav-arrow">→</span>
            </button>
          </aside>

          <section class="admin-content">
            <div v-if="filter === 'printer'" class="printer-settings">
              <div class="admin-section-heading">
                <span class="section-overline">Hardware</span>
                <h2>Printer settings</h2>
                <p>Choose the printer used for visitor badges.</p>
              </div>
              <div class="printer-card">
                <span class="printer-icon"
                  ><IconGlyph name="printer" :size="24"
                /></span>
                <div class="printer-copy">
                  <strong>Visitor badge printer</strong
                  ><small>Used when a guest completes sign-in.</small>
                </div>
                <div class="select-wrap printer-select-wrap">
                  <select v-model="selectedPrinter" class="kiosk-select">
                    <option disabled value="">Select a printer</option>
                    <option
                      v-for="printer in allPrinters"
                      :key="printer"
                      :value="printer"
                      >{{ printer }}</option
                    >
                  </select>
                  <IconGlyph name="chevron-down" :size="18" />
                </div>
              </div>
              <div class="printer-note">
                <IconGlyph name="check" :size="15" /><span
                  >Printer preference is saved on this kiosk.</span
                >
              </div>
            </div>

            <div v-else class="history-section">
              <div class="admin-section-heading admin-section-heading--row">
                <div>
                  <span class="section-overline">{{ activeLabel }}</span>
                  <h2>{{ activeTitle }}</h2>
                </div>
                <label class="admin-search"
                  ><IconGlyph name="search" :size="17"/><input
                    v-model="search"
                    type="search"
                    placeholder="Search records"
                /></label>
              </div>
              <v-data-table
                :headers="activeHeaders"
                :items="historyDocs"
                :options.sync="options"
                :search="search"
                :server-items-length="history.totalItems || -1"
                :footer-props="{ itemsPerPageOptions: [5, 10, 25] }"
                class="admin-table"
                dense
              ></v-data-table>
            </div>
          </section>
        </div>
      </div>
    </template>
  </Container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Container from "@/components/Container";
import IconGlyph from "@/components/IconGlyph";

export default {
  name: "Admin",
  components: { Container, IconGlyph },
  data() {
    return {
      filter: "guest",
      type: "guest",
      filters: [
        { text: "Guest history", value: "guest", icon: "user" },
        { text: "Pickup history", value: "pickup", icon: "package" },
        { text: "Delivery history", value: "delivery", icon: "truck" },
        { text: "Printer settings", value: "printer", icon: "printer" }
      ],
      search: "",
      guestHeaders: [
        { text: "Name", value: "name" },
        { text: "Company", value: "company" },
        { text: "Reason", value: "reason" },
        { text: "Guest of", value: "employee" },
        { text: "Date", sortable: true, value: "date" }
      ],
      pickupHeaders: [
        { text: "Company / carrier", value: "company" },
        { text: "Pickup / delivery", value: "reason" },
        { text: "Packages / pallets", value: "packages", sortable: true },
        { text: "Date", sortable: true, value: "date" }
      ],
      options: { page: 1, itemsPerPage: 5, sortBy: ["date"], sortDesc: [true] }
    };
  },
  computed: {
    ...mapGetters({
      history: "getHistory",
      allPrinters: "getAllPrinters",
      storedPrinter: "getPrinter"
    }),
    historyDocs() {
      return this.history.docs || [];
    },
    activeHeaders() {
      return this.filter === "guest" ? this.guestHeaders : this.pickupHeaders;
    },
    activeLabel() {
      return this.filter === "guest"
        ? "Visitor activity"
        : "Logistics activity";
    },
    activeTitle() {
      const current = this.filters.find(item => item.value === this.filter);
      return current ? current.text : "History";
    },
    selectedPrinter: {
      get() {
        return this.storedPrinter;
      },
      set(value) {
        this.$store.commit("setPrinter", value);
      }
    },
    searchParams() {
      return {
        ...this.options,
        search: this.search || null,
        type: this.type || null
      };
    }
  },
  watch: {
    filter(value) {
      if (value !== "printer") {
        this.type = value;
        this.getHistory(this.searchParams);
      }
    },
    search() {
      if (this.filter !== "printer") this.getHistory(this.searchParams);
    },
    options: {
      handler() {
        if (this.filter !== "printer") this.getHistory(this.searchParams);
      },
      deep: true
    }
  },
  mounted() {
    this.refreshData();
  },
  methods: {
    ...mapActions({
      getHistory: "getHistory",
      getAllPrinters: "getAllPrinters"
    }),
    refreshData() {
      this.getAllPrinters();
      if (this.filter !== "printer") this.getHistory(this.searchParams);
    }
  }
};
</script>

<style>
.admin-page {
  padding-bottom: 25px;
}

.admin-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin: 0 0 27px;
}

.admin-heading h1 {
  margin: 7px 0 6px;
  color: var(--ink);
  font-family: Eurosoft, -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 42px;
  font-weight: 500;
  letter-spacing: -0.045em;
}

.admin-heading p,
.admin-section-heading p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

.refresh-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 41px;
  padding: 0 14px;
  border: 1px solid #d9dfda;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.6);
  color: var(--ink);
  font: inherit;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
}

.refresh-button:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.admin-layout {
  display: grid;
  grid-template-columns: 205px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.admin-nav,
.admin-content {
  border: 1px solid rgba(30, 41, 42, 0.08);
  border-radius: 19px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 45px rgba(30, 41, 42, 0.055);
}

.admin-nav {
  padding: 15px 10px;
}

.admin-nav-label {
  display: block;
  margin: 7px 11px 11px;
  color: #9ba39e;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.admin-nav-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;
  gap: 10px;
  padding: 0 11px;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: var(--muted);
  font: inherit;
  font-size: 11px;
  font-weight: 800;
  text-align: left;
  cursor: pointer;
}

.admin-nav-item + .admin-nav-item {
  margin-top: 3px;
}

.admin-nav-item:hover,
.admin-nav-item--active {
  background: var(--accent-soft);
  color: var(--accent);
}

.nav-arrow {
  margin-left: auto;
  font-size: 18px;
}

.admin-content {
  min-height: 400px;
  padding: 30px;
  box-sizing: border-box;
}

.admin-section-heading {
  margin-bottom: 22px;
}

.admin-section-heading h2 {
  margin: 7px 0 5px;
  color: var(--ink);
  font-size: 21px;
  font-weight: 850;
  letter-spacing: -0.035em;
}

.admin-section-heading--row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.admin-search {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 205px;
  height: 38px;
  padding: 0 11px;
  box-sizing: border-box;
  border: 1px solid #e0e4df;
  border-radius: 10px;
  color: var(--accent);
}

.admin-search input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 11px;
}

.admin-table {
  overflow: hidden;
  border: 1px solid #e7ebe6;
  border-radius: 13px;
  background: transparent !important;
}

.admin-table .v-data-table__wrapper {
  min-height: 240px;
}

.admin-table table {
  color: var(--ink) !important;
}

.admin-table th {
  height: 44px !important;
  background: #f3f1ed !important;
  color: var(--muted) !important;
  font-size: 10px !important;
  font-weight: 850 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.admin-table td {
  height: 50px !important;
  color: var(--ink) !important;
  font-size: 11px !important;
}

.admin-table tr:hover {
  background: #fbfcfa !important;
}

.printer-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 19px;
  border: 1px solid #e4e9e4;
  border-radius: 15px;
  background: #fbfcfa;
}

.printer-icon {
  display: grid;
  width: 45px;
  height: 45px;
  border-radius: 13px;
  background: var(--accent-soft);
  color: var(--accent);
  place-items: center;
}

.printer-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
}

.printer-copy strong {
  color: var(--ink);
  font-size: 12px;
}

.printer-copy small {
  color: var(--muted);
  font-size: 10px;
}

.printer-select-wrap {
  width: min(45%, 300px);
}

.printer-select-wrap .kiosk-select {
  min-height: 46px;
}

.printer-select-wrap > .icon-glyph {
  top: 14px;
}

.printer-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 15px;
  color: var(--muted);
  font-size: 10px;
}

.printer-note .icon-glyph {
  color: var(--accent);
}

@media (max-width: 800px) {
  .admin-layout {
    grid-template-columns: 1fr;
  }

  .admin-nav {
    display: flex;
    align-items: center;
    gap: 4px;
    overflow-x: auto;
  }

  .admin-nav-label {
    display: none;
  }

  .admin-nav-item {
    min-width: max-content;
  }
}

@media (max-width: 620px) {
  .admin-heading,
  .admin-section-heading--row,
  .printer-card {
    align-items: stretch;
    flex-direction: column;
  }

  .admin-heading h1 {
    font-size: 37px;
  }

  .admin-search,
  .printer-select-wrap {
    width: 100%;
  }

  .admin-content {
    padding: 19px;
  }
}
</style>
